import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Eye, Mail, Pencil, Plus, Power } from 'lucide-react';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import { Panel } from '@/components/Panel';
import { AuditDate } from '@/components/AuditDate';
import { StatusBadge } from '@/components/dashboard/DashboardParts';
import { ReferenceOptions } from '@/components/form/ReferenceOptions';
import { FilterSelect } from '@/components/list/FilterSelect';
import { ListStatus } from '@/components/list/ListStatus';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SearchInput } from '@/components/list/SearchInput';
import { IconCell } from '@/components/table/IconCell';
import { Pagination } from '@/components/table/Pagination';
import { PersonCell } from '@/components/table/PersonCell';
import { RowActionsMenu } from '@/components/table/RowActionsMenu';
import { SortableHeader } from '@/components/table/SortableHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { getApiErrorMessage } from '@/lib/apiError';
import UserModel from '@/models/entities/UserModel';
import { useListControls } from '@/hooks/useListControls';
import { usePagedList } from '@/hooks/usePagedList';
import { useIsCurrentUser } from '@/security/useIsCurrentUser';
import {
  buildUserListRequest,
  DEFAULT_USER_FILTERS,
  DEFAULT_USER_SORT,
  type ReferenceIdResolver,
} from './userListQuery';
import { findReferenceId, useReferenceOptions, useReferences } from '@/stores/useReferenceOptions';
import { UserFormDialog } from './UserFormDialog';
import { useIamManagedUserIds } from './iamManaged';
import { getManualChangeBlocker, toggleUserStatus } from './userStatus';
import { appRoleIcon } from '@/components/roles/roleIcons';

/** Gestion des utilisateurs (cahier des charges §4.4, prototype « Gestion des Utilisateurs ») */
export function UsersPage() {
  const isCurrentUser = useIsCurrentUser();
  const list = useListControls({
    filters: DEFAULT_USER_FILTERS,
    sort: DEFAULT_USER_SORT,
  });
  const { filters, search, sort, page, itemsPerPage, changeFilters, changeSort } = list;
  const [editing, setEditing] = useState<{ user: UserModel | null } | null>(null);

  /** Incrémenté pour recharger la page (après création, modification, changement de statut) */
  const [version, setVersion] = useState(0);
  const reload = () => setVersion((v) => v + 1);

  // Filtres en références (ADMIN, ENABLED…), envoyés à l'API par leur id
  const appRoles = useReferenceOptions('appRoles');
  const userStatuses = useReferenceOptions('userStatuses');
  const toId = useCallback<ReferenceIdResolver>(
    (name, reference) => findReferenceId(name === 'appRoles' ? appRoles : userStatuses, reference),
    [appRoles, userStatuses],
  );
  const request = useMemo(
    () => buildUserListRequest({ ...filters, search }, sort, page, itemsPerPage, toId),
    [filters, search, sort, page, itemsPerPage, toId],
  );
  // Rôle et statut des utilisateurs : lus par leur id dans les listes de référence (chargées une fois par session)
  useReferences('appRoles', 'userStatuses');
  const { models: users, totalItems, loading, error: loadError } = usePagedList(UserModel, request, version);

  const isMe = (user: UserModel) => isCurrentUser(user.id);
  // Utilisateurs gérés par leur IAM (statut non modifiable à la main) : leurs identités, une requête par page
  const iamManaged = useIamManagedUserIds(users);
  const statusBlocker = (user: UserModel) =>
    getManualChangeBlocker(iamManaged.has(Number(user.id)), isMe(user), 'status');

  const toggleStatus = async (user: UserModel) => {
    try {
      toast.success(await toggleUserStatus(user));
      reload();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const onSaved = (user: UserModel) => {
    toast.success(editing?.user ? `${user.fullName} a été modifié.` : `${user.fullName} a été créé.`);
    setEditing(null);
    reload();
  };

  return (
    <Panel className="overflow-hidden">
      <ListToolbar
        search={
          <SearchInput
            label="Rechercher un utilisateur"
            value={filters.search}
            onChange={(value) => changeFilters({ search: value })}
          />
        }
      >
        <FilterSelect
          label="Filtrer par rôle"
          allLabel="Tous les rôles"
          value={filters.appRole}
          onChange={(appRole) => changeFilters({ appRole })}
        >
          <ReferenceOptions name="appRoles" />
        </FilterSelect>
        <FilterSelect
          label="Filtrer par statut"
          allLabel="Tous les statuts"
          value={filters.userStatus}
          onChange={(userStatus) => changeFilters({ userStatus })}
        >
          <ReferenceOptions name="userStatuses" />
        </FilterSelect>
        <Button onClick={() => setEditing({ user: null })}>
          <Plus className="w-4 h-4" />
          Nouvel utilisateur
        </Button>
      </ListToolbar>

      <div className="overflow-x-auto">
        {/* Page précédente gardée à l'écran, estompée, pendant le chargement de la suivante */}
        <table
          className={clsx('w-full text-left border-collapse transition-opacity', loading && 'opacity-60')}
          aria-busy={loading}
        >
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortableHeader
                className="px-6 py-4"
                label="Utilisateur"
                sortKey="name"
                sort={sort}
                onSort={changeSort}
              />
              <SortableHeader className="px-6 py-4" label="Email" sortKey="email" sort={sort} onSort={changeSort} />
              <th className="px-6 py-4">Rôle</th>
              <th className="px-6 py-4">Statut</th>
              <SortableHeader
                className="px-6 py-4"
                label="Ajouté le"
                sortKey="createdAt"
                sort={sort}
                onSort={changeSort}
              />
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-slate-50/80 transition-colors" data-testid="user-row">
                <PersonCell
                  to={`/users/${user.id}`}
                  firstName={user.firstName}
                  lastName={user.lastName}
                  fullName={user.fullName}
                  suffix={isMe(user) && '(vous)'}
                />
                <IconCell icon={Mail}>{user.email}</IconCell>
                <IconCell icon={appRoleIcon(user.appRoleReference)}>{user.appRoleLabel}</IconCell>
                <td className="px-6 py-4">
                  <StatusBadge enabled={user.isEnabled} />
                </td>
                <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                  <AuditDate date={user.createdAt} by={user.createdBy} />
                </td>
                <RowActionsMenu label={`Actions pour ${user.fullName}`}>
                  <DropdownMenuItem asChild>
                    <Link to={`/users/${user.id}`}>
                      <Eye className="w-4 h-4" />
                      Voir la fiche
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setEditing({ user })}>
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => toggleStatus(user)}
                    disabled={!!statusBlocker(user)}
                    title={statusBlocker(user) ?? undefined}
                  >
                    <Power className="w-4 h-4" />
                    {user.isEnabled ? 'Désactiver' : 'Réactiver'}
                  </DropdownMenuItem>
                </RowActionsMenu>
              </tr>
            ))}
          </tbody>
        </table>
        <ListStatus
          className="p-6"
          loading={loading && users.length === 0}
          error={loadError}
          empty={!loading && users.length === 0 && 'Aucun utilisateur ne correspond à ces critères.'}
        />
      </div>

      <Pagination {...list.pagination} totalItems={totalItems} label={['utilisateur', 'utilisateurs']} />

      {editing && (
        <UserFormDialog
          key={editing.user?.id ?? 'new'}
          user={editing.user}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </Panel>
  );
}
